import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useParams } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Trash2, ExternalLink, Image, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PortfolioGallery = () => {
  const { user } = useAuth();
  const { userId } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newItem, setNewItem] = useState({
    title: '',
    description: '',
    image_url: '',
    project_url: '',
    tags: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const isOwnPortfolio = !userId || userId === user?.id;

  useEffect(() => {
    fetchPortfolio();
  }, [userId]);

  const fetchPortfolio = async () => {
    try {
      const url = isOwnPortfolio
        ? `${API}/portfolio/my`
        : `${API}/portfolio/user/${userId}`;
      const res = await axios.get(url);
      setItems(res.data);
    } catch (error) {
      console.error('Failed to fetch portfolio', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      let imageUrl = newItem.image_url;
      if (imageFile) {
        const formData = new FormData();
        formData.append('file', imageFile);
        const uploadRes = await axios.post(`${API}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        imageUrl = uploadRes.data.url;
      }
      if (!imageUrl) {
        toast.error('Please upload an image or provide an image URL');
        setUploading(false);
        return;
      }
      await axios.post(`${API}/portfolio`, {
        ...newItem,
        image_url: imageUrl,
        tags: newItem.tags ? newItem.tags.split(',').map(t => t.trim()) : []
      });
      toast.success('Portfolio item added!');
      setShowAddDialog(false);
      setNewItem({ title: '', description: '', image_url: '', project_url: '', tags: '' });
      setImageFile(null);
      fetchPortfolio();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add portfolio item');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Remove this portfolio item?')) return;
    try {
      await axios.delete(`${API}/portfolio/${itemId}`);
      toast.success('Portfolio item removed');
      fetchPortfolio();
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-lg">Loading portfolio...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navigation />
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-primary mb-2" data-testid="portfolio-title">
              {isOwnPortfolio ? 'My Portfolio' : 'Portfolio Gallery'}
            </h1>
            <p className="text-muted-foreground">
              {isOwnPortfolio ? 'Showcase your best work to attract clients' : 'Browse portfolio items'}
            </p>
          </div>
          {isOwnPortfolio && user?.role === 'freelancer' && (
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="bg-secondary text-secondary-foreground" data-testid="add-portfolio-button">
                  <Plus className="w-4 h-4 mr-2" /> Add Work
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Add Portfolio Item</DialogTitle>
                  <DialogDescription>Showcase a completed project or work sample</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAddItem} className="space-y-4" data-testid="add-portfolio-form">
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={newItem.title}
                      onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                      required
                      placeholder="E-commerce Website Redesign"
                      data-testid="portfolio-title-input"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={newItem.description}
                      onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                      required
                      rows={3}
                      placeholder="Describe the project, your role, and the outcome..."
                      data-testid="portfolio-description-input"
                    />
                  </div>
                  <div>
                    <Label>Project Image</Label>
                    <div className="space-y-2">
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setImageFile(e.target.files?.[0])}
                        data-testid="portfolio-image-upload"
                      />
                      <p className="text-xs text-muted-foreground">Or paste an image URL:</p>
                      <Input
                        value={newItem.image_url}
                        onChange={(e) => setNewItem({ ...newItem, image_url: e.target.value })}
                        placeholder="https://example.com/screenshot.png"
                        data-testid="portfolio-image-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Project URL (optional)</Label>
                    <Input
                      value={newItem.project_url}
                      onChange={(e) => setNewItem({ ...newItem, project_url: e.target.value })}
                      placeholder="https://example.com"
                      data-testid="portfolio-url-input"
                    />
                  </div>
                  <div>
                    <Label>Tags (comma separated)</Label>
                    <Input
                      value={newItem.tags}
                      onChange={(e) => setNewItem({ ...newItem, tags: e.target.value })}
                      placeholder="React, UI/UX, E-commerce"
                      data-testid="portfolio-tags-input"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={uploading} data-testid="portfolio-submit-button">
                    {uploading ? 'Uploading...' : 'Add to Portfolio'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {items.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <FolderOpen className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-heading font-semibold mb-2">No portfolio items yet</h3>
              <p className="text-muted-foreground">
                {isOwnPortfolio
                  ? 'Add your first project to start building your portfolio!'
                  : 'This user has not added any portfolio items yet.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((item) => (
              <Card key={item.id} className="overflow-hidden hover:shadow-lg transition-all hover:-translate-y-1" data-testid={`portfolio-item-${item.id}`}>
                <div className="aspect-video bg-slate-100 overflow-hidden">
                  <img
                    src={item.image_url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${item.image_url}` : item.image_url}
                    alt={item.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.classList.add('flex', 'items-center', 'justify-center');
                      const icon = document.createElement('div');
                      icon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" class="text-muted-foreground"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>';
                      e.target.parentElement.appendChild(icon);
                    }}
                  />
                </div>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-heading">{item.title}</CardTitle>
                  <CardDescription className="line-clamp-2">{item.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {item.tags?.map((tag, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">{tag}</Badge>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    {item.project_url && (
                      <a
                        href={item.project_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-secondary hover:underline"
                        data-testid={`portfolio-link-${item.id}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5" /> View Project
                      </a>
                    )}
                    {isOwnPortfolio && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-auto text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeleteItem(item.id)}
                        data-testid={`delete-portfolio-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PortfolioGallery;
